import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import { selectGameConfig } from "src/store/common"
import { selectGame, selectValidUnitsForPlayer, selectViewerId } from "src/store/game"
import { cellDisplayName } from "src/utils/maps"
import { dischargeOrderAt } from "src/utils/cellActions"
import { dischargeOrderedAt, dischargeSummary } from "src/utils/pendingOrders"
import ModalService from "src/modals"

import Modal from "src/components/modals/Modal"
import UnitPicker, { takesText } from "src/components/UnitPicker"

import "./AttackModal.css"
import "./DischargeModal.css"

/* DAR DE BAJA (owner, 2026-09-30): humanity stands units down into workers on the cell they stand
   on, for no action — one worker a body of flesh, none for a tank or a helicopter. It is how
   humanity moves its coin between cities: march the soldiers, discharge them where they stand. On
   ground nobody owns too (owner, 2026-10-02), where the workers pay for a colony on that cell.
   There is no room to run out of anywhere: the population ceiling stops a command centre's growth
   and never deletes a worker (owner, 2026-10-02), so every body standing there may stand down.

   **The confirm is an order** (owner, 2026-10-23: *"humanos dar de baja Tropas sucede la próxima
   ronda"*, requirement 36 D): nothing changes on the cell until the round turns, and the bodies
   named are held until then. The sheet offers only the FREE bodies — `selectGame` has already
   taken what a pending discharge (or a tunnel entry) holds off the garrison
   (`pendingOrders.effectiveForPlayer`), so a second order here picks from what is left and joins
   the first on the server. The head says what is already retiring; the roster row and the city
   sheet carry the ✕ that takes it back (`RetiringNote`), and so does the pick on the map.

   The tunnel sheet's frame (`.march-head`, `.march-foot`) around the shared `UnitPicker`: sliders,
   typed counts, Tab walks the counts, Space confirms. The head's chip is the workers the pick
   gives. Both numbers it needs come from `GET /api/game-config/` — `biological_units` and
   `human_config.DISCHARGE_WORKERS_PER_BODY`. Whether the order may be given here at
   all is the map bar's entry (`dischargeOrderAt` → `cellActions`). The server is the gate
   (`simulator/use_cases/discharge_units.py`). */

const WORKER = "worker"

const perBodyOf = (gameConfig) => gameConfig?.human_config?.DISCHARGE_WORKERS_PER_BODY ?? 0
const isFlesh = (gameConfig, type) => (gameConfig?.biological_units || []).includes(type)

/* The workers a pick gives: `DISCHARGE_WORKERS_PER_BODY` a body of flesh, nothing for a machine. */
export const dischargeYield = ({ gameConfig, troops }) =>
  Object.entries(troops || {}).reduce(
    (sum, [type, number]) => (isFlesh(gameConfig, type) ? sum + (Number(number) || 0) * perBodyOf(gameConfig) : sum),
    0
  )

/* A share of every stack, in order. */
export const dischargeFill = ({ pool, fraction }) => {
  const troops = {}
  pool.forEach(({ type, available }) => {
    troops[type] = Math.floor(available * fraction)
  })
  return troops
}

const DischargeModal = ({ city }) => {
  const { t } = useTranslation()
  const game = useSelector(selectGame)
  const gameConfig = useSelector(selectGameConfig)
  const userId = useSelector(selectViewerId)
  const validUnits = useSelector(selectValidUnitsForPlayer)

  const key = city ? `${city.col}-${city.row}` : null
  const cells = game?.metadata?.cells || {}
  // The free garrison: what a discharge already ordered here holds is off it (`effectiveForPlayer`).
  const garrison = key ? cells[key]?.metadata_by_user?.[String(userId)] : null
  // The map bar's order for this cell: whether it may be given here, and why not.
  const order = dischargeOrderAt({ city, game, gameConfig, userId })
  // A city of ours is named as one; ground nobody owns as the camp it is.
  const held = order?.held !== false
  // What is already retiring here, said in the head so a second pick reads against it.
  const pending = city ? dischargeOrderedAt({ metadata: game?.metadata, userId, col: city.col, row: city.row }) : null
  const already = pending ? dischargeSummary({ order: pending, gameConfig }) : null

  // The garrison without its workers, in the class's order as the march lists it.
  const rank = (type) => (validUnits.includes(type) ? validUnits.indexOf(type) : validUnits.length)
  const pool = Object.entries(garrison?.units || {})
    .filter(([type]) => type !== WORKER)
    .map(([type, data]) => ({ type, available: data?.number ?? 0 }))
    .filter(({ available }) => available > 0)
    .sort((a, b) => rank(a.type) - rank(b.type))

  // Opens with nobody chosen: the order can be taken back, but a pick made by default is a pick
  // the player did not make.
  const [troops, setTroops] = useState({})

  const units = pool.map(({ type, available }) => ({
    type,
    available,
    max: available,
  }))
  const setUnitNumber = (type, number) =>
    setTroops((current) => {
      const available = pool.find((unit) => unit.type === type)?.available ?? 0
      return { ...current, [type]: Math.max(0, Math.min(available, Math.floor(Number(number) || 0))) }
    })
  const fillAll = (fraction) => setTroops(dischargeFill({ pool, fraction }))

  const chosen = Object.entries(troops).filter(([, number]) => Number(number) > 0)
  const picked = chosen.reduce((sum, [, number]) => sum + Number(number), 0)
  const gained = dischargeYield({ gameConfig, troops })
  const reason = !order
    ? t("city.dischargeNothing")
    : order.reason
      ? t(`game.mapAction.reason.${order.reason.key}`, order.reason)
      : null
  const blocked = Boolean(reason) || picked === 0

  const send = () => {
    if (blocked) return
    const sent = wsClient.send("game", {
      action: "discharge_units",
      column: Number(city.col),
      row: Number(city.row),
      troops: Object.fromEntries(chosen),
    })
    if (sent) ModalService.hide()
  }

  // Space sends, as it does from the march and the tunnel — also straight after a slider or a
  // typed count. Only while this sheet is on top.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== " ") return
      if (takesText(document.activeElement)) return
      const stack = ModalService.stackTypes ?? []
      if (stack.length && stack[stack.length - 1] !== "DischargeModal") return
      event.preventDefault()
      send()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  })

  const word = t("game.mapAction.discharge")
  const name = city ? cellDisplayName({ row: city.row, column: city.col, held }) : ""

  return (
    <Modal width="min(560px, 96%)" isolated={false} scope="map" top="4vh" maxHeight="100%">
      <header className="march-head discharge-head">
        <h1>
          🪖 {word}
          {name ? ` · ${name}` : ""}
        </h1>
        <span
          className="march-kind discharge-chip"
          data-testid="discharge-gain"
        >
          +{gained} 👷
        </span>
      </header>

      {already && (
        <p className="discharge-pending" data-testid="discharge-pending">
          {t("city.retiring", { count: already.bodies, workers: already.workers })}
        </p>
      )}

      <UnitPicker
        className="discharge-force"
        label={t("city.garrison")}
        units={units}
        value={troops}
        onChange={setUnitNumber}
        onFill={pool.length > 0 ? fillAll : undefined}
        emptyText={t("city.dischargeNothing")}
      />

      <footer className="march-foot">
        <button className="march-send discharge-send" data-action="discharge" onClick={send} disabled={blocked}>
          {reason || (picked === 0 ? t("attack.pickYourForce") : `🪖 ${word} · +${gained} 👷 · ${t("city.nextRound")}`)}
        </button>
        <button className="quiet march-cancel" onClick={() => ModalService.hide()}>
          {t("attack.cancel")}
        </button>
      </footer>
    </Modal>
  )
}

export default DischargeModal
